import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useInvestigations } from '../hooks/useInvestigations'
import EmptyState from '../components/EmptyState'
import ErrorState from '../components/ErrorState'
import LoadingOverlay from '../components/LoadingOverlay'
import { IntegrationLogo } from '../components/IntegrationLogo'
import { ApiRequestError, type Investigation } from '../api/client'

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'badge-error',
  high:     'badge-error',
  medium:   'badge-warning',
  low:      'badge-success',
}

const STATUS_BADGE: Record<string, string> = {
  complete: 'badge-success',
  running: 'badge-info',
  failed: 'badge-error',
  stale: 'badge-warning',
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Generate deterministic mockup service name based on alert title
function getServiceTag(alertName: string): { service: string; env: string } {
  const name = alertName.toLowerCase();
  let service = 'checkout-service';
  if (name.includes('db') || name.includes('postgres') || name.includes('pool')) service = 'db-transaction-v2';
  else if (name.includes('memory') || name.includes('oom') || name.includes('heap')) service = 'image-resize-worker';
  else if (name.includes('gateway') || name.includes('api')) service = 'api-gateway';
  else if (name.includes('auth') || name.includes('jwt')) service = 'user-auth-service';

  const env = name.includes('staging') || name.includes('dev') ? 'staging-us' : 'prod-us-east';
  return { service, env };
}

const INTEGRATION_PATTERNS: Array<{ pattern: RegExp; id: string; name: string }> = [
  { pattern: /vercel/i, id: 'vercel', name: 'Vercel' },
  { pattern: /sentry/i, id: 'sentry', name: 'Sentry' },
  { pattern: /postgres(ql)?/i, id: 'postgresql', name: 'PostgreSQL' },
  { pattern: /datadog/i, id: 'datadog', name: 'Datadog' },
  { pattern: /grafana/i, id: 'grafana', name: 'Grafana' },
  { pattern: /pagerduty/i, id: 'pagerduty', name: 'PagerDuty' },
  { pattern: /github/i, id: 'github', name: 'GitHub' },
  { pattern: /gitlab/i, id: 'gitlab', name: 'GitLab' },
  { pattern: /\baws\b|amazon/i, id: 'aws', name: 'AWS' },
  { pattern: /kubernetes|k8s/i, id: 'kubernetes', name: 'Kubernetes' },
  { pattern: /prometheus/i, id: 'prometheus', name: 'Prometheus' },
  { pattern: /slack/i, id: 'slack', name: 'Slack' },
  { pattern: /splunk/i, id: 'splunk', name: 'Splunk' },
  { pattern: /opsgenie/i, id: 'opsgenie', name: 'OpsGenie' },
  { pattern: /honeycomb/i, id: 'honeycomb', name: 'Honeycomb' },
  { pattern: /newrelic|new relic/i, id: 'newrelic', name: 'New Relic' },
]

const INTEGRATION_DISPLAY_NAMES: Record<string, string> = {
  vercel: 'Vercel', sentry: 'Sentry', postgresql: 'PostgreSQL', datadog: 'Datadog',
  grafana: 'Grafana', pagerduty: 'PagerDuty', github: 'GitHub', gitlab: 'GitLab',
  aws: 'AWS', kubernetes: 'Kubernetes', prometheus: 'Prometheus', slack: 'Slack',
  splunk: 'Splunk', opsgenie: 'OpsGenie', honeycomb: 'Honeycomb', newrelic: 'New Relic',
}

function getIntegrationSource(alertName: string, alertSource?: string): { id: string; name: string } | null {
  if (alertSource && alertSource !== 'unknown') {
    const name = INTEGRATION_DISPLAY_NAMES[alertSource] ?? alertSource
    return { id: alertSource, name }
  }
  for (const { pattern, id, name } of INTEGRATION_PATTERNS) {
    if (pattern.test(alertName)) return { id, name }
  }
  return null
}

function ActiveInvestigationCard({ inv }: { inv: Investigation }) {
  const { service, env } = getServiceTag(inv.alert_name)
  const integration = getIntegrationSource(inv.alert_name, inv.alert_source)
  return (
    <div className="hypothesis-card active" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div className="hypothesis-header">
        <Link to={`/investigations/${inv.id}`} className="inv-link" style={{ fontSize: '14px', fontWeight: 600 }}>
          {inv.alert_name}
        </Link>
        <span
          className={`badge ${STATUS_BADGE[inv.status] ?? 'badge-info'}`}
          style={inv.status === 'running' ? { animation: 'pulse-border 2s infinite ease-in-out' } : undefined}
        >
          {inv.status}
        </span>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {integration && (
          <span className="conn-type-badge" style={{ fontSize: '10px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <IntegrationLogo integrationId={integration.id} size={14} />
            {integration.name}
          </span>
        )}
        <span className="conn-type-badge conn-type-cli" style={{ fontSize: '10px' }}>📁 {service}</span>
        <span className="conn-type-badge conn-type-cli" style={{ fontSize: '10px' }}>☁️ {env}</span>
        <span className={`badge ${SEVERITY_BADGE[inv.severity.toLowerCase()] ?? 'badge-info'}`} style={{ fontSize: '10px' }}>
          {inv.severity}
        </span>
      </div>

      <div className="step-progress-wrapper" style={{ width: '100%', marginTop: '4px' }}>
        <div className="step-progress-text">
          <span>Investigating</span>
          <strong>{inv.step_count ?? 0} {(inv.step_count ?? 0) === 1 ? 'step' : 'steps'} run</strong>
        </div>
        <div className="step-progress-track">
          <div className="step-progress-fill running" style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Triggered {relativeTime(inv.created_at)}</span>
        <Link to={`/investigations/${inv.id}`} className="view-btn">
          Open Console →
        </Link>
      </div>
    </div>
  )
}

function HistoricalRow({ inv }: { inv: Investigation }) {
  const { service, env } = getServiceTag(inv.alert_name)
  const integration = getIntegrationSource(inv.alert_name, inv.alert_source)
  const stepCount = inv.step_count ?? 0
  const stepLabel = `${stepCount} ${stepCount === 1 ? 'step' : 'steps'}`
  // An in-flight remediation attempt (e.g. after a Retry) supersedes the terminal status:
  // show the investigation as actively remediating in both the status and progress columns.
  const remediating = inv.remediation_status === 'pending' || inv.remediation_status === 'running'
  const progress = remediating
    ? { label: 'Remediating', detail: stepLabel, width: '100%', color: '', running: true }
    : inv.status === 'failed'
      ? { label: 'Refuted', detail: stepLabel, width: '50%', color: 'var(--error)', running: false }
      : inv.status === 'stale'
        ? { label: 'Stalled', detail: stepLabel, width: '50%', color: 'var(--warning)', running: false }
        : { label: 'Completed', detail: stepLabel, width: '100%', color: 'var(--success)', running: false }

  return (
    <tr>
      <td>
        <Link to={`/investigations/${inv.id}`} className="inv-link" style={{ fontWeight: 600 }}>
          {inv.alert_name}
        </Link>
        <div style={{ display: 'flex', gap: '8px', marginTop: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="conn-type-badge conn-type-cli" style={{ fontSize: '9px', padding: '1px 5px' }}>📁 {service}</span>
          <span className="conn-type-badge conn-type-cli" style={{ fontSize: '9px', padding: '1px 5px' }}>☁️ {env}</span>
        </div>
      </td>
      <td>
        {integration ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <IntegrationLogo integrationId={integration.id} size={18} />
            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-h)' }}>{integration.name}</span>
          </div>
        ) : (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>—</span>
        )}
      </td>
      <td>
        <span className={`badge ${SEVERITY_BADGE[inv.severity.toLowerCase()] ?? 'badge-info'}`}>
          {inv.severity}
        </span>
      </td>
      <td>
        {remediating ? (
          <span className="badge badge-warning" style={{ animation: 'pulse-border 2s infinite ease-in-out' }}>
            remediating
          </span>
        ) : (
          <span className={`badge ${STATUS_BADGE[inv.status] ?? 'badge-info'}`}>
            {inv.status}
          </span>
        )}
      </td>
      <td>
        <div className="step-progress-wrapper">
          <div className="step-progress-text">
            <span>{progress.label}</span>
            <strong>{progress.detail}</strong>
          </div>
          <div className="step-progress-track">
            <div
              className={`step-progress-fill${progress.running ? ' running' : ''}`}
              style={{ width: progress.width, ...(progress.color ? { background: progress.color } : {}) }}
            />
          </div>
        </div>
      </td>
      <td>
        <span title={new Date(inv.created_at).toLocaleString()}>
          {relativeTime(inv.created_at)}
        </span>
      </td>
      <td style={{ textAlign: 'right' }}>
        <Link to={`/investigations/${inv.id}`} className="view-btn">
          RCA Report →
        </Link>
      </td>
    </tr>
  )
}

export default function InvestigationList() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [severityFilter, setSeverityFilter] = useState('all')

  const { data, isLoading, isError, error, refetch } = useInvestigations(page)

  if (isLoading) return <LoadingOverlay minHeight="240px" />
  if (isError) {
    const apiErr = error instanceof ApiRequestError ? error : null
    const message =
      apiErr?.code === 'invalid_issuer'
        ? 'Your session issuer is not accepted by the API. Sign out and back in, or contact support.'
        : apiErr?.code === 'token_expired'
          ? 'Session expired. Refresh the page or sign in again.'
          : `Failed to load investigations: ${(error as Error).message}`
    return <ErrorState message={message} onRetry={() => void refetch()} />
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / 20)

  if (total === 0 && items.length === 0 && !search && statusFilter === 'all' && severityFilter === 'all') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="text-display-md" style={{ margin: 0 }}>
            Investigations
          </h1>
        </div>
        <EmptyState
          message="No investigations yet for this workspace."
          hint="Connect Sentry or Vercel on Integrations, or trigger an alert — new incidents appear here automatically."
        />
      </div>
    )
  }

  // Apply search/filters
  const filteredItems = items.filter((inv) => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false
    if (severityFilter !== 'all' && inv.severity.toLowerCase() !== severityFilter) return false
    if (search && !inv.alert_name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  // Separate running and completed/failed
  const activeIncidents = filteredItems.filter((inv) => inv.status === 'running')
  const historyIncidents = filteredItems.filter((inv) => inv.status !== 'running')

  return (
    <div className="page">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="text-display-md" style={{ margin: 0 }}>Investigations</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
            Track active and resolved incident investigations, from alert to root cause to proposed fix.
          </p>
        </div>
      </div>

      {/* ── Search & Filter Controls ─────────────────────────── */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search incident alerts or service endpoints..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="running">Running</option>
          <option value="complete">Complete</option>
          <option value="failed">Failed</option>
        </select>

        <select
          className="filter-select"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* ── ACTIVE RUNNING INCIDENTS SECTION ───────────────────── */}
      {activeIncidents.length > 0 && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-h)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
            🚨 Active SRE Triage ({activeIncidents.length})
          </h2>
          <div className="hypotheses-grid">
            {activeIncidents.map((inv) => (
              <ActiveInvestigationCard key={inv.id} inv={inv} />
            ))}
          </div>
        </section>
      )}

      {/* ── HISTORICAL INCIDENTS SECTION ──────────────────────── */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
        <h2 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-h)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
          📂 Investigation History ({historyIncidents.length})
        </h2>

        {historyIncidents.length === 0 ? (
          <EmptyState
            message="No historical records found."
            hint="Try clearing your search/filter filters."
          />
        ) : (
          <>
            <table className="inv-table">
              <thead>
                <tr>
                  <th>Alert / Component</th>
                  <th>Platform</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Triage progress</th>
                  <th>Triggered</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {historyIncidents.map((inv) => (
                  <HistoricalRow key={inv.id} inv={inv} />
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pagination" style={{ marginTop: '16px' }}>
                <button
                  className="page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Previous
                </button>
                <span className="page-info">
                  Page {page} / {totalPages}
                </span>
                <button
                  className="page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
